import { AxiosResponse } from 'axios'
import {
    OrderContract,
    OrderLineContract,
    OrderLineStatus,
    OrderPaymentStatus,
    OrderLookupContract,
    OrderStatus,
    OrderDetailContract,
} from 'models'
import { searchOrderRequest } from 'store/actions/orderLookup.action'
import pendingOrderDbService from 'utilities/offlineDb/pendingOrderDbService'
import { v4 } from 'uuid'
import { convertPendingRecordToSlimModel } from './modelConvertor'
import { ISaleDependencies } from 'views/Sale/contexts/SaleDependenciesContext'
import { ProcessSaleContract } from 'models/ProcessSaleContract'

export interface OfflineOrderLineContract extends OrderLineContract {
    itemName: string
}

export interface OfflineOrderContract
    extends Omit<OrderContract, 'orderLines'> {
    orderLines: OfflineOrderLineContract[]
    customerName: string
}

export const processOfflineOrder = async (
    payload: OfflineOrderContract,
): Promise<AxiosResponse<ProcessSaleContract>> => {
    if (!payload.orderID) payload.orderID = v4()

    await pendingOrderDbService.saveOrder(payload)

    const response: ProcessSaleContract = {
        order: {
            orderID: payload.orderID,
            status: OrderStatus.Saved,
            serviceChargeRate: payload.serviceChargeRate,
        },
    }

    const axiosResponse: AxiosResponse<ProcessSaleContract> = {
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
    }

    return Promise.resolve(axiosResponse)
}

export const getOfflineOrder = async (
    refSellState: ISaleDependencies,
    orderID: string,
): Promise<OrderDetailContract> => {
    const pendingOrder = (await pendingOrderDbService.getById(orderID))!
    return convertPendingRecordToSlimModel(refSellState, pendingOrder)
}

/**
 * Load saved offline orders in iDB, then converted to OrderLookupContract
 * @param searchRequest
 * @returns list of orders in OrderLookupContract
 */
export const getOfflineOrders = async (
    dependencies: ISaleDependencies,
    searchRequest: searchOrderRequest,
): Promise<OrderLookupContract[]> => {
    const pendingOrders = await pendingOrderDbService.getAll()

    return pendingOrders.map((o) => {
        const model = convertPendingRecordToSlimModel(dependencies, o)
        model.balanceTotalDue = model.totalDueAmount
        return model
    })
}

export const voidOfflineOrder = async (order: OrderContract) => {
    const pendingOrder = await pendingOrderDbService.getById(order.orderID!)
    if (!pendingOrder) return

    pendingOrder.orderStatus = OrderStatus.Voided
    pendingOrder.orderPaymentStatus = OrderPaymentStatus.Voided
    pendingOrder.order.voidComp = order.voidComp
    pendingOrder.order.orderLines.forEach((line) => {
        line.status = OrderLineStatus.Voided
    })

    await pendingOrderDbService.update(pendingOrder)
}
